/**
 * Capture — prototype VIEWS.capture + capRender (docs/prototype.html L823–951), opened from the Capture button in
 * the top bar on every screen.
 *
 *   idle / rec   Record a voice note or type/paste → "Create tasks from this" (POST /capture/notes).
 *                ADR-002 §24.5: the note is dictated live through the browser's speech service (lib/speech,
 *                LiveRecorder): words appear as the member speaks, at most 2:00, with the vendor notice beside
 *                Record. The member edits the text, and only the text is sent (text_origin 'browser' +
 *                transcript_source 'live' + audio_seconds). Firefox has no recogniser: the member types.
 *   proposing    The configured language model reads the note.
 *   proposed     The countdown ring (settle_at): statements for client notes, tasks with confidence and
 *                Auto-approve / Held, escalations to tick. Confirm now / Edit (pause) / Cancel.
 *   done         "Added to Ops." with what each item became, and Undo all while undo_all_until is open.
 *
 * Every number comes from GET /capture/notes/{id}; the countdown runs off the server's settle_at.
 */
import { useEffect, useMemo, useState } from 'react';
import type { CaptureItem, CaptureNote } from '@amplispot/contracts';
import { useSession } from '../../lib/session';
import { useClients, useUsers } from '../../lib/data';
import { firstName, plural } from '../../lib/format';
import {
  ago,
  confTone,
  mmss,
  useAutoLog,
  useCancelNote,
  useCaptureNote,
  useCaptureSettings,
  useConfirmNote,
  useCreateNote,
  useHeld,
  usePause,
  useResume,
  useRotateCaptureLink,
  useUndoNote,
  useUpdateItem,
} from '../../lib/capture';
import { ErrorState, Loading, Modal, errorText, useToast } from '../../ui/kit';
import { PageInfo } from '../../shell/Help';
import { HeldList } from './HeldList';
import { TOO_SHORT, appendWords, useVoiceRecorder } from './useVoiceRecorder';
import { Hearing, LiveRecorder, mss } from './LiveRecorder';

const NOTE_KEY = 'capture.note';

export function Capture() {
  const { can } = useSession();
  const settings = useCaptureSettings();
  const [noteId, setNoteId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(NOTE_KEY);
    } catch {
      return null;
    }
  });
  const remember = (id: string | null) => {
    setNoteId(id);
    try {
      if (id) sessionStorage.setItem(NOTE_KEY, id);
      else sessionStorage.removeItem(NOTE_KEY);
    } catch {
      /* per-tab convenience only */
    }
  };
  const isMgr = can('tasks.read.all');

  return (
    <>
      <div className="ph">
        <div>
          <h1>
            Capture
            <PageInfo helpKey="capture" />
          </h1>
          <p>Speak or type. We turn it into statements and tasks, and you get {inWords(settings.data?.countdown_seconds ?? 120)} to check them.</p>
        </div>
        <div className="r">{settings.data ? <PersonalLink active={settings.data.capture_link_active} issuedAt={settings.data.capture_link_issued_at} /> : null}</div>
      </div>
      <div className="cols c21">
        {/* Prototype: the card is as tall as its content, not the right column. */}
        <div className="panel" id="capBody" style={{ alignSelf: 'start' }}>
          {noteId ? <NoteBody id={noteId} onReset={() => remember(null)} /> : <Recorder onCreated={(id) => remember(id)} />}
        </div>
        <div style={{ display: 'grid', gap: 16, alignContent: 'start' }}>
          <div className="panel">
            <div className="hd">
              <h3>How it works</h3>
            </div>
            <div className="bd" style={{ display: 'grid', gap: 12 }}>
              {[
                ['1', 'Talk', 'Open your link on your phone. No app, no login each time.'],
                ['2', 'Check', `Statements and tasks show up with a confidence score. You have ${settings.data ? countdownWords(settings.data.countdown_seconds) : '2 minutes'}.`],
                ['3', 'Done', 'Confident items go in on their own. Unsure ones wait for a person.'],
              ].map(([n, t, s]) => (
                <div key={n} className="split" style={{ alignItems: 'flex-start' }}>
                  <span className="chip brand" style={{ marginTop: 1 }}>
                    {n}
                  </span>
                  <div>
                    <b>{t}</b>
                    <div style={{ color: 'var(--sub)', fontSize: 13 }}>{s}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <RecentCaptures />
          {!isMgr ? <MyHeld /> : null}
        </div>
      </div>
    </>
  );
}

/**
 * Prototype "Use a sample note" (capSample). The prototype's text names its demo clients, but here the data is
 * real: a processed note settles on its own, creating tasks and writing statements with a client into that
 * client's notes (capture engine settleCore). So the sample is about the member's own work only. It names no
 * client, makes no claim about how anyone feels, and has no complaint or escalation, so trying it can only give
 * the member a few tasks of their own.
 */
const SAMPLE_NOTE =
  'Remind me to fill my timesheet by 6 pm today. I need to go through the new onboarding checklist by Friday, and block an hour on Thursday to tidy my notes from this week.';

const WORDS: Record<number, string> = { 1: 'one', 2: 'two', 3: 'three' };
/** Prototype head: "you get two minutes to check them". */
const inWords = (s: number) => (s % 60 === 0 && WORDS[s / 60] ? `${WORDS[s / 60]} minute${s === 60 ? '' : 's'}` : countdownWords(s));
const countdownWords = (s: number) => (s % 60 === 0 ? plural(s / 60, 'minute') : plural(s, 'second'));

// ---------------------------------------------------------------- idle / recording

function Recorder({ onCreated }: { onCreated: (id: string) => void }) {
  const toast = useToast();
  const create = useCreateNote();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Live dictation through the browser's speech service (ADR-002 §24.5), shared with the capture link page so the
  // two cannot drift. Words are appended at the end, so the member's earlier edits are never overwritten.
  const v = useVoiceRecorder({ onFinal: (said) => setText((t) => appendWords(t, said)) });

  const submit = async () => {
    const t = text.trim();
    if (v.recording) {
      toast('Stop the recording first');
      return;
    }
    if (!t) {
      toast('Say or type something first');
      return;
    }
    if (t.length < 3) {
      setErr(TOO_SHORT);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      // Only the text the member checked goes, never audio: dictated notes say so and how long they spoke.
      const r = await create.mutateAsync({ text: t, ...v.outgoing() });
      onCreated(r.note_id);
    } catch (e) {
      setErr(e instanceof Error && !('status' in e) ? e.message : errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const clear = () => {
    v.reset();
    setText('');
    setErr(null);
  };

  return (
    <>
      <div className="hd">
        <h3>New note</h3>
        {text.trim() && !v.recording ? (
          <div className="r">
            <button type="button" className="btn sm ghost" onClick={clear}>
              Start again
            </button>
          </div>
        ) : null}
      </div>
      <div className="bd">
        <LiveRecorder v={v} disabled={busy} />
        <div className="txhead">
          <span className="lbl">{v.recording ? 'Transcript · you can edit it, even while recording' : v.rec === 'stopped' ? 'Transcript · you can edit it' : 'Your note'}</span>
          <div className="grow" />
          <span className={`cnt${err === TOO_SHORT ? ' bad' : ''}`}>
            {text.length} characters{v.rec === 'stopped' && v.usedS > 0 ? ` · ${mss(v.usedS)} recorded` : ''}
          </span>
        </div>
        <textarea
          className="in"
          id="capTx"
          rows={v.rec === 'idle' ? 4 : 6}
          aria-label="Transcript"
          placeholder={v.voice && v.why !== 'blocked' ? 'Words appear here as you speak. You can also type or paste.' : 'Type or paste your note.'}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setErr(null);
          }}
          ref={(el) => {
            // While recording, the editor follows the newest words.
            if (el && v.recording && document.activeElement !== el) el.scrollTop = el.scrollHeight;
          }}
          maxLength={5000}
        />
        <Hearing v={v} />
        {text === SAMPLE_NOTE ? (
          <div className="sub" style={{ color: 'var(--sub)', fontSize: 12.5, marginTop: 6 }}>
            Sample — edit before processing. As it stands it only makes tasks for you.
          </div>
        ) : null}
        {err ? (
          <div className="note bad" style={{ marginTop: 10 }}>
            {err}
          </div>
        ) : null}
        <div className="split" style={{ flexWrap: 'wrap', marginTop: 12 }}>
          {!text.trim() && !v.recording ? (
            <button type="button" className="btn sm" onClick={() => setText(SAMPLE_NOTE)}>
              Use a sample note
            </button>
          ) : null}
          <span className="sub" style={{ color: 'var(--sub)', fontSize: 12.5 }}>
            {v.recording ? 'Stop the recording to send. You can fix the text while it records.' : 'Only the text you check is saved. Amplispot does not receive or keep audio.'}
          </span>
          <div className="grow" />
          <button className="btn pri" onClick={() => void submit()} disabled={busy || v.recording}>
            {busy ? 'Sending…' : 'Create tasks from this'}
          </button>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- one note, after it is sent

function NoteBody({ id, onReset }: { id: string; onReset: () => void }) {
  const q = useCaptureNote(id);
  const toast = useToast();
  if (q.isPending) return <Loading label="Opening your note…" />;
  if (q.isError) {
    return (
      <div style={{ padding: 16 }}>
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
        <button className="btn" style={{ marginTop: 10 }} onClick={onReset}>
          Record another
        </button>
      </div>
    );
  }
  const n = q.data;
  if (n.state === 'cancelled') {
    return <Cancelled onReset={onReset} />;
  }
  if (n.state === 'proposing') return <Working note={n} />;
  if (n.state === 'done') return <Done note={n} onReset={onReset} />;
  return <Counting note={n} onCancelled={() => toast('Cancelled. Nothing was added.')} />;
}

function Cancelled({ onReset }: { onReset: () => void }) {
  return (
    <div style={{ padding: '26px 20px' }}>
      <div className="note" style={{ marginBottom: 14 }}>
        <b>Cancelled.</b> Nothing was added.
      </div>
      <button className="btn pri" onClick={onReset}>
        Record another
      </button>
    </div>
  );
}

function Working({ note: n }: { note: CaptureNote }) {
  return (
    <div className="mic-wrap" style={{ padding: '40px 20px' }}>
      <span className="spin" aria-hidden="true" />
      <div style={{ fontWeight: 600, fontSize: 16 }}>Reading your note</div>
      <div style={{ color: 'var(--sub)', textAlign: 'center', maxWidth: 420 }}>Finding the tasks, the client and the dates in what you said.</div>
      {n.transcript ? (
        <div className="note" style={{ maxWidth: 520, whiteSpace: 'pre-wrap' }}>
          {n.transcript.text}
        </div>
      ) : null}
    </div>
  );
}

/** Re-renders every second for the countdown. */
function useNow(ms = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(t);
  }, [ms]);
  return now;
}

function Counting({ note: n, onCancelled }: { note: CaptureNote; onCancelled: () => void }) {
  const now = useNow();
  const toast = useToast();
  const { can } = useSession();
  const isMgr = can('users.read');
  const paused = n.state === 'editing';
  const total = n.note.countdown_seconds;
  const left = paused
    ? n.note.paused_remaining_seconds ?? 0
    : n.settle_at
      ? Math.max(0, (new Date(n.settle_at).getTime() - now) / 1000)
      : total;
  const pct = Math.min(100, Math.max(0, 100 - (left / total) * 100));
  const thr = n.note.auto_threshold;
  const tasks = n.items.filter((i) => i.kind === 'task');
  const stmts = n.items.filter((i) => i.kind === 'statement');
  const escs = n.items.filter((i) => i.kind === 'escalation');
  const auto = tasks.filter((t) => (t.will ? t.will === 'auto_create' : t.confidence >= thr));
  const held = tasks.length - auto.length;

  const pause = usePause();
  const resume = useResume();
  const cancel = useCancelNote();
  const confirm = useConfirmNote();
  const update = useUpdateItem();
  const clients = useClients();
  const users = useUsers(paused && isMgr);
  const people = useMemo(() => (users.data ?? []).filter((u) => u.status === 'active' && ['founder', 'ops_ad', 'ops_member'].includes(u.role)), [users.data]);

  const fail = (e: unknown) => toast(errorText(e));
  const edit = (it: CaptureItem, body: Parameters<typeof update.mutate>[0]['body']) =>
    update.mutate({ noteId: n.note.id, itemId: it.id, version: n.note.version, body }, { onError: fail });
  // An escalation tick shows at once; the server's value takes over when the note's version moves on (or the
  // write fails). The box is held while a write is in flight, so a second tick never goes out on a stale version.
  const [raiseLocal, setRaiseLocal] = useState<Record<string, boolean>>({});
  useEffect(() => setRaiseLocal({}), [n.note.version]);
  const tick = (e: CaptureItem, raise: boolean) => {
    setRaiseLocal((m) => ({ ...m, [e.id]: raise }));
    update.mutate(
      { noteId: n.note.id, itemId: e.id, version: n.note.version, body: { raise } },
      {
        onError: (err) => {
          setRaiseLocal({});
          fail(err);
        },
      },
    );
  };

  return (
    <>
      <div style={{ padding: '18px 16px', display: 'flex', gap: 18, alignItems: 'center', borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
        <div className="ring" style={{ ['--p' as string]: pct }}>
          <div>{mmss(left)}</div>
        </div>
        <div className="grow" style={{ minWidth: 220 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{paused ? 'Timer paused while you edit' : left > 0 ? `Adding these to Ops in ${mmss(left)}` : 'Adding these to Ops now…'}</div>
          <div style={{ color: 'var(--sub)', marginTop: 2 }}>
            {plural(auto.length, 'high-confidence task')} will be approved automatically. {held ? `${held} will wait in the review queue for a person.` : ''}
          </div>
        </div>
        <div className="split">
          <button
            className="btn good"
            disabled={confirm.isPending}
            onClick={() =>
              confirm.mutate(
                { id: n.note.id, item_ids: tasks.map((t) => t.id), escalation_item_ids: escs.filter((e) => e.raise).map((e) => e.id) },
                { onError: fail },
              )
            }
          >
            Confirm now
          </button>
          <button
            className="btn"
            disabled={pause.isPending || resume.isPending}
            onClick={() => (paused ? resume.mutate(n.note.id, { onError: fail }) : pause.mutate(n.note.id, { onError: fail }))}
          >
            {paused ? 'Resume timer' : 'Edit'}
          </button>
          <button className="btn bad" disabled={cancel.isPending} onClick={() => cancel.mutate(n.note.id, { onSuccess: onCancelled, onError: fail })}>
            Cancel
          </button>
        </div>
      </div>
      <div className="cols c2" style={{ padding: 16 }}>
        <div>
          <div className="lbl" style={{ marginBottom: 8 }}>
            Statements being added to client notes
          </div>
          {stmts.length ? (
            stmts.map((s) => (
              <div key={s.id} className="li" style={{ padding: '9px 0' }}>
                <div className="b">
                  <div className="t" style={{ fontWeight: 500 }}>
                    {s.title}
                  </div>
                  <div className="m">{s.client ? s.client.name : 'No client found'}</div>
                </div>
              </div>
            ))
          ) : (
            <div className="sub" style={{ color: 'var(--sub)' }}>
              None found.
            </div>
          )}
        </div>
        <div>
          <div className="lbl" style={{ marginBottom: 8 }}>
            Tasks being created
          </div>
          {tasks.length ? (
            tasks.map((t) => {
              const willAuto = t.will ? t.will === 'auto_create' : t.confidence >= thr;
              return (
                <div key={t.id} className="li" style={{ padding: '9px 0' }}>
                  <div className="b">
                    {paused ? (
                      <>
                        <input
                          className="in"
                          defaultValue={t.title}
                          style={{ marginBottom: 6 }}
                          onBlur={(e) => e.target.value.trim() !== t.title && e.target.value.trim().length >= 3 && edit(t, { title: e.target.value.trim() })}
                          aria-label="Task"
                        />
                        <div className="split">
                          {isMgr ? (
                            <select className="in" value={t.assignee?.id ?? ''} onChange={(e) => e.target.value && edit(t, { assignee_id: e.target.value })} aria-label="Who">
                              {!t.assignee ? <option value="">No one yet</option> : null}
                              {people.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name}
                                </option>
                              ))}
                              {t.assignee && !people.some((p) => p.id === t.assignee!.id) ? <option value={t.assignee.id}>{t.assignee.name}</option> : null}
                            </select>
                          ) : null}
                          <select className="in" value={t.client?.id ?? ''} onChange={(e) => edit(t, { client_id: e.target.value || null })} aria-label="Client">
                            <option value="">No client</option>
                            {(clients.data ?? []).map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                          <input
                            className="in"
                            type="date"
                            value={t.due ?? ''}
                            onChange={(e) => edit(t, { due: e.target.value || null })}
                            aria-label="Due"
                            style={{ width: 'auto' }}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="t" style={{ fontWeight: 500 }}>
                          {t.title}
                        </div>
                        <div className="m">
                          <span>{t.assignee ? firstName(t.assignee.name) : 'No one yet'}</span>
                          <span>{t.client ? t.client.name : t.client_guess ? `“${t.client_guess}”?` : 'No client'}</span>
                          <span>{t.due ? dueWords(t.due) : 'No date'}</span>
                        </div>
                      </>
                    )}
                  </div>
                  {/* Narrow, so a long held reason wraps here instead of pushing the meta line (who · client · due) onto two. */}
                  <div style={{ textAlign: 'right', maxWidth: 112, flexShrink: 0 }}>
                    <span className={`conf ${confTone(t.confidence, thr)}`}>{t.confidence}%</span>
                    <div style={{ fontSize: 11, color: 'var(--sub)', marginTop: 3 }}>{willAuto ? 'Auto-approve' : 'Held'}</div>
                    {t.why_held && !willAuto ? <div style={{ fontSize: 11, color: 'var(--warm)', lineHeight: 1.35 }}>{t.why_held}</div> : null}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="sub" style={{ color: 'var(--sub)' }}>
              No tasks found in this note.
            </div>
          )}
        </div>
      </div>
      {escs.length ? (
        <div style={{ padding: '0 16px 16px' }}>
          <div className="note warn">
            <b>This sounds like an escalation.</b> We never raise these on our own. Tick the ones to raise.
            {escs.map((e) => (
              <label key={e.id} className="split" style={{ marginTop: 8, alignItems: 'flex-start' }}>
                <input type="checkbox" checked={raiseLocal[e.id] ?? e.raise} disabled={update.isPending} onChange={(ev) => tick(e, ev.target.checked)} style={{ marginTop: 3 }} />
                <span>
                  <b>{e.client ? e.client.name : e.client_guess ?? 'No client'}</b>: {e.title}{' '}
                  <span className={`chip ${e.severity === 'high' ? 'cold' : 'warm'}`}>{e.severity === 'high' ? 'High' : 'Medium'}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      ) : null}
      {n.transcript ? (
        <details style={{ padding: '0 16px 16px' }}>
          <summary className="sub" style={{ cursor: 'pointer', color: 'var(--sub)', fontSize: 12.5 }}>
            What you said
          </summary>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 13.5, marginTop: 8, background: 'var(--canvas)', padding: 12, borderRadius: 10 }}>{n.transcript.text}</div>
        </details>
      ) : null}
    </>
  );
}

const STAMP: Partial<Record<CaptureItem['outcome'], [string, string]>> = {
  confirmed: ['hot', 'Confirmed'],
  auto_created: ['hot', 'Auto-approved'],
  held: ['warm', 'Held'],
  undone: ['', 'Undone'],
};

function Done({ note: n, onReset }: { note: CaptureNote; onReset: () => void }) {
  const now = useNow(15000);
  const undo = useUndoNote();
  const toast = useToast();
  const s = n.note.summary;
  const tasks = n.items.filter((i) => i.kind === 'task');
  const how = s
    ? [
        s.confirmed ? `${s.confirmed} confirmed by you.` : '',
        s.auto_created ? `${s.auto_created} auto-approved after the timer.` : '',
        s.held ? `${s.held} held for review.` : '',
        `${plural(s.statements_noted, 'statement')} saved to client notes.`,
        s.escalations_raised ? `${plural(s.escalations_raised, 'escalation')} raised.` : '',
      ]
        .filter(Boolean)
        .join(' ')
    : '';
  const canUndo = !!n.note.undo_all_until && new Date(n.note.undo_all_until).getTime() > now;
  return (
    <div style={{ padding: '26px 20px' }}>
      <div className="note good" style={{ marginBottom: 14 }}>
        <b>Added to Ops.</b> {how}
      </div>
      {tasks.map((t) => {
        const st = STAMP[t.outcome];
        return (
          <div key={t.id} className="li" style={{ padding: '9px 0' }}>
            <div className="b">
              <div className="t" style={{ fontWeight: 500 }}>
                {t.title}
              </div>
              <div className="m">
                {t.assignee ? firstName(t.assignee.name) : 'No one yet'} · {t.client ? t.client.name : 'No client'}
              </div>
            </div>
            {st ? <span className={`chip ${st[0]}`}>{st[1]}</span> : null}
          </div>
        );
      })}
      <div className="split" style={{ marginTop: 14 }}>
        {canUndo ? (
          <button
            className="btn"
            disabled={undo.isPending}
            onClick={() =>
              undo.mutate(n.note.id, {
                onSuccess: () => {
                  toast('Undone. Tasks removed.');
                  onReset();
                },
                onError: (e) => toast(errorText(e)),
              })
            }
          >
            Undo all
          </button>
        ) : null}
        <div className="grow" />
        <button className="btn pri" onClick={onReset}>
          Record another
        </button>
      </div>
    </div>
  );
}

/** "Today", "Tomorrow", "Wed 7 Oct". */
function dueWords(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - t.getTime()) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }).replace(',', '');
}

// ---------------------------------------------------------------- right column

function RecentCaptures() {
  const q = useAutoLog(true, 4);
  const now = Date.now();
  return (
    <div className="panel">
      <div className="hd">
        <h3>Your recent captures</h3>
      </div>
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <div style={{ padding: 12 }}>
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        </div>
      ) : q.data.data.length ? (
        q.data.data.slice(0, 4).map((a) => (
          <div key={a.task_id} className="li">
            <div className="b">
              <div className="t" style={{ fontWeight: 500 }}>
                {a.title}
              </div>
              <div className="m">
                {ago(a.created_at, now)} <span className="conf h">{a.confidence}%</span>
                {a.undone ? <span className="chip">Undone</span> : null}
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="empty">No captures yet</div>
      )}
    </div>
  );
}

/** A member resolves what they captured: their own held items, with the same Confirm / Discard as the Review queue. */
function MyHeld() {
  const q = useHeld();
  const n = q.data?.length ?? 0;
  // Prototype: the member's column has no such card; it appears only while something of theirs waits (errors still show).
  if (q.isPending || (q.isSuccess && n === 0)) return null;
  return (
    <div className="panel">
      <div className="hd">
        <h3>Waiting for a person</h3>
        <div className="r">{n ? <span className="chip warm">{n}</span> : null}</div>
      </div>
      <HeldList q={q} compact />
    </div>
  );
}

// ---------------------------------------------------------------- personal link

/**
 * Prototype head chip "Personal link: ops.amplispot.com/c/…". The server keeps only a hash of the token, so the URL
 * can be shown once, when it is made (POST /users/{id}/capture-token/rotate). The chip opens a modal that makes
 * a new link and shows it with Copy; making one turns off the old link.
 */
function PersonalLink({ active, issuedAt }: { active: boolean; issuedAt: string | null }) {
  const { readOnly } = useSession();
  const [open, setOpen] = useState(false);
  const label = active ? `Personal link: on${issuedAt ? ` since ${new Date(issuedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}` : 'Personal link: get yours';
  return (
    <>
      <button type="button" className="chip brand" style={{ cursor: 'pointer' }} onClick={() => setOpen(true)} disabled={readOnly} aria-haspopup="dialog">
        {label}
      </button>
      {open ? <PersonalLinkModal active={active} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function PersonalLinkModal({ active, onClose }: { active: boolean; onClose: () => void }) {
  const { me } = useSession();
  const toast = useToast();
  const rotate = useRotateCaptureLink();
  const [url, setUrl] = useState<string | null>(null);
  const make = () => rotate.mutate(me.user.id, { onSuccess: (r) => setUrl(r.capture_url) });
  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast('Link copied');
    } catch {
      toast('Select the link and copy it');
    }
  };
  return (
    <Modal
      title="Your personal link"
      sub="Open it on your phone to capture a note. No app, no login each time."
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            {url ? 'Done' : 'Cancel'}
          </button>
          {url ? (
            <button className="btn pri" onClick={copy}>
              Copy link
            </button>
          ) : (
            <button className="btn pri" onClick={make} disabled={rotate.isPending}>
              {rotate.isPending ? 'Making your link…' : active ? 'Make a new link' : 'Make my link'}
            </button>
          )}
        </>
      }
    >
      {url ? (
        <div style={{ display: 'grid', gap: 10 }}>
          <input className="in" readOnly value={url} onFocus={(e) => e.currentTarget.select()} aria-label="Your personal link" style={{ fontFamily: 'var(--mono, monospace)' }} />
          <div className="note">We show this link once. Save it on your phone now; if you lose it, make a new one here.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          <p style={{ margin: 0 }}>
            {active
              ? 'You already have a link. We cannot show it again. Making a new link turns the old one off straight away.'
              : 'Make a link and keep it on your phone. Anything you say there comes here to check, like a note recorded on this screen.'}
          </p>
          {rotate.isError ? <ErrorState error={rotate.error} onRetry={make} /> : null}
        </div>
      )}
    </Modal>
  );
}
